/**
 * updatePrompt - decides when to show the "new version of Worthly" pop-up.
 *
 * The backend (/api/app-version) reads the version live on the App Store and
 * only says "ready" 2 days after a new one first appears, since most phones
 * auto-update by then. Nothing needs setting when a release goes out.
 *
 * Shown once per version, then again every 3+ days after "Later" until
 * they update. Stored on the phone only.
 */
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';

const KEY = 'worthly_update_prompt';
const REMIND_AFTER_DAYS = 3;
const APP_STORE_URL = 'itms-apps://apps.apple.com/app/id6761525480';

// '1.10.2' vs '1.9' -> 1 (a is newer), 0 same, -1 older
export const compareVersions = (a, b) => {
  const pa = String(a || '').split('.').map((p) => parseInt(p, 10) || 0);
  const pb = String(b || '').split('.').map((p) => parseInt(p, 10) || 0);
  const n = Math.max(pa.length, pb.length, 3);
  for (let i = 0; i < n; i += 1) {
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x !== y) return x > y ? 1 : -1;
  }
  return 0;
};

const readState = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
};

// Resolves to the newer version to prompt for, or null.
export const checkForUpdate = async (apiUrl) => {
  try {
    if (!Capacitor.isNativePlatform()) return null;
    if (new Date().getDate() <= 7) return null; // month-end review week
    const info = await CapApp.getInfo();
    const current = info && info.version;
    if (!current) return null;

    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 6000);
    let data = null;
    try {
      const res = await fetch(`${apiUrl}/api/app-version`, { signal: ctrl.signal });
      if (!res.ok) return null;
      data = await res.json();
    } finally {
      clearTimeout(timer);
    }

    const latest = data && data.latest_version;
    if (!latest || !data.ready) return null;
    if (compareVersions(latest, current) <= 0) return null;

    const s = readState();
    if (s.version === latest) {
      if (Date.now() - (s.lastShownAt || 0) < REMIND_AFTER_DAYS * 86400000) return null;
    }
    return latest;
  } catch (e) {
    return null;
  }
};

export const markUpdatePromptShown = (version) => {
  try {
    const s = readState();
    const shows = s.version === version ? (s.shows || 0) + 1 : 1;
    localStorage.setItem(KEY, JSON.stringify({ version, shows, lastShownAt: Date.now() }));
  } catch (e) {
    // Storage unavailable - it may show again next open, which is harmless.
  }
};

export const openAppStorePage = () => {
  try {
    window.location.href = APP_STORE_URL;
  } catch (e) {
    // Nothing else to try.
  }
};
